import { useEffect, useMemo, useState } from 'react';
import { useRecentMessage } from '@modules/channels/hooks/use-recent-message2';
import { useContacts } from '@modules/contacts/use-contacts';
import { useDebounce } from '@shared/hooks/use-debounce';
import { ContactItem } from '../components/bulk-messaging/bulk-messaging.types';
import { useNavigate } from 'react-router-dom';
import { ContactSourceTypeEnum } from '@/types/generated/graphql';
import { getCountryNameFromPhoneNumber } from '@shared/utils/country.helper';
import { getAvatarSource } from '@shared/utils/avatar.helper';

const RECENT_CONTACTS_LIMIT = 5;

export type BulkMessagingStep = 'select' | 'review';

export const useBulkMessaging = (search = '', initialContacts: ContactItem[] = []) => {
  const [step, setStep] = useState<BulkMessagingStep>('select');
  const [recipients, setRecipients] = useState<ContactItem[]>(initialContacts);
  const isReview = step === 'review';
  const debouncedSearch = useDebounce(search, 1500);
  const navigate = useNavigate();

  // const selectedPhoneNumbers = recipients.map((contact) => contact.phoneNumber);
  // Backend only accepts clientIds (numeric contact IDs), not ad-hoc phone numbers yet.
  // Filter to entries with an id; once backend adds support for raw phone numbers, remove this filter.
  const selectedContactIds = recipients.filter((r) => r.id != null).map((contact) => contact.id as string);

  const recentMessage = useRecentMessage({ query: '' });
  const { contactList } = useContacts({
    query: debouncedSearch.trim(),
    searchBy: ['name', 'phoneNumber'],
    sourceType: Object.values(ContactSourceTypeEnum),
  });
  const { data, loading, hasNextPage, loadNextRef } = contactList;
  const isSearching = loading || search.trim() !== debouncedSearch.trim();

  useEffect(() => {
    if (initialContacts.length === 0) return;
    setRecipients(initialContacts);
  }, [initialContacts]);

  const recentContacts = useMemo<ContactItem[]>(
    () =>
      (recentMessage.data ?? []).slice(0, RECENT_CONTACTS_LIMIT).map((item) => ({
        id: String(item.client?.id),
        name: item.name ?? 'Unknown',
        phoneNumber: item.client?.phoneNumber ?? '+1',
        countryName: 'Unknown',
        avatar: item.avatarUrl ?? undefined,
      })),
    [recentMessage.data]
  );

  const contacts = useMemo<ContactItem[]>(
    () =>
      (data?.data ?? []).map((item) => ({
        id: String(item?.id),
        name: item?.name ?? 'Unknown',
        phoneNumber: item?.phoneNumber ?? '+1',
        countryName: item?.country?.id != null ? String(item.country.id) : 'Unknown',
        avatar: item?.country?.flag ?? item?.profilePicture?.url ?? undefined,
      })),
    [data?.data]
  );

  const reviewContacts = useMemo<ContactItem[]>(() => recipients, [recipients]);

  const handleContactToggle = (contact: ContactItem) => {
    setRecipients((prev) => {
      const exists = prev.some((c) => c.phoneNumber === contact.phoneNumber);
      if (exists) {
        return prev.filter((c) => c.phoneNumber !== contact.phoneNumber);
      }
      return [...prev, contact];
    });
  };

  const handleAddNumber = (phoneNumber: string) => {
    setRecipients((prev) => {
      const exists = prev.some((c) => c.phoneNumber === phoneNumber);
      if (exists) return prev;

      const countryName = getCountryNameFromPhoneNumber(phoneNumber) ?? 'Unknown';
      const avatar = getAvatarSource({ phoneNumber });
      return [...prev, { phoneNumber, countryName, avatar }];
    });
  };

  const handleRemoveRecipient = (phoneNumber: string) => {
    setRecipients((prev) => prev.filter((r) => r.phoneNumber !== phoneNumber));
  };

  const handleBulkToggle = (contactsPage: ContactItem[]) => {
    setRecipients((prev) => {
      const existing = new Set(prev.map((r) => r.phoneNumber));
      const allSelected = contactsPage.length > 0 && contactsPage.every((c) => existing.has(c.phoneNumber));

      if (allSelected) {
        const toRemove = new Set(contactsPage.map((c) => c.phoneNumber));
        return prev.filter((r) => !toRemove.has(r.phoneNumber));
      }

      return [...prev, ...contactsPage.filter((c) => !existing.has(c.phoneNumber))];
    });
  };

  const goToReview = () => setStep('review');
  const goToSelect = () => setStep('select');

  const goToCompose = () => {
    navigate('/bulk-messaging/compose-message', { state: { reviewContacts } });
  };

  const reset = () => {
    setStep('select');
    setRecipients([]);
  };

  return {
    isReview,
    goToReview,
    goToSelect,
    goToCompose,
    reset,
    recentContacts,
    contacts,
    isSearching,
    hasNextPage,
    loadNextRef,
    selectedContactIds,
    handleContactToggle,
    reviewContacts,
    handleAddNumber,
    handleRemoveRecipient,
    handleBulkToggle,
  };
};
